import * as Y from 'yjs';
import {connectHosanna} from './HosannaConnection';
import {WebsocketProvider} from 'y-websocket';
import {getConnectionInfo, getDeviceInfo} from '../storage/localStorage';
import {SessionManager} from '../storage/SessionManager';
import {generateGuid} from '../../shared/services/utils';
import {diffChars} from 'diff';
import {IndexeddbPersistence} from 'y-indexeddb';
import {isWorkspaceReadOnly, assertWorkspaceWritable} from '../../shared/services/workspaceAccess';

export class YjsDocumentService {
  constructor() {
    this.docs = new Map();
    this.providers = new Map();
    this.observers = new Map();
    this.documentObservers = new Map();
    this.awarenessObservers = new Map();

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    this.serverUrl = process.env.REACT_APP_YJS_SERVER_URL || `${protocol}//${host}/ws`;
    this.projectionEpoch = null;

  }

  connectToDocument(wsId, filePath) {
    if (!wsId) throw new Error('Workspace ID is required');
    if (process.env.REACT_APP_HOSANNA_DIRECT === '1') return connectHosanna(this, wsId, filePath);

    // First, check if we already have a document for this workspace ID
    if (this.docs.has(wsId)) {
      const doc = this.docs.get(wsId);
      const provider = this.providers.get(wsId);

      // If no file path, just return the doc and provider
      if (!filePath) {
        return { doc, provider };
      }

      // With file path, return doc, provider, text, and metadata
      const contentPath = `content:${filePath}`;
      const metadataPath = `metadata:${filePath}`;
      return {
        doc,
        text: doc.getText(contentPath),
        metadata: doc.getMap(metadataPath),
        provider,
        filePath,
        contentPath,
        metadataPath
      };
    }

    // If no existing document, create a new one
    try {
      // Initialize Y.Doc and persistence
      const doc = new Y.Doc();
      // Adorans used to be editable. Its historical local Yjs updates can win
      // over the live mirror even though the server now rejects client writes.
      // Keep this readonly document authoritative from the bridge, without
      // importing the old editable cache or updates from an older local tab.
      if (!isWorkspaceReadOnly(wsId)) {
        new IndexeddbPersistence(wsId, doc).on('synced', () =>
          console.log(`Workspace ${wsId} loaded from IndexedDB persistence`)
        );
      }

      // Validate workspace ID
      if (!wsId.startsWith('songs-')) {
        throw new Error(`Invalid workspace ID: ${wsId}. It should start with 'songs-'`);
      }

      // Set up provider with device info
      const deviceInfo = getDeviceInfo();
      const provider = new WebsocketProvider(
        this.serverUrl,
        wsId,
        doc,
        {
          connect: process.env.REACT_APP_HOSANNA_READONLY !== '1'
            || `songs-storage-${SessionManager.getCredentialSession()?.id}` === wsId,
          disableBc: isWorkspaceReadOnly(wsId),
          params: {deviceGuid: deviceInfo.guid}
        }
      );

      // Set awareness user info
      if (provider.awareness) {
        provider.awareness.setLocalStateField('user', {
          name: deviceInfo.name || 'Anonymous', id: deviceInfo.guid
        });
      }

      // Store doc and provider
      this.docs.set(wsId, doc);
      this.providers.set(wsId, provider);

      // Set up event logging
      provider.on('status', e => {
        console.log(`Workspace ${wsId} connection status:`, e.status);
        if (e.status === 'connected' && process.env.REACT_APP_HOSANNA_READONLY === '1') {
          // Recreated compatibility views have new CRDT identities. Never merge
          // a previous adapter epoch into the new view after a server restart.
          fetch('/adorans/status', {cache: 'no-store'})
            .then(response => response.ok ? response.json() : null)
            .then(status => {
              if (!status?.epoch) return;
              if (this.projectionEpoch && this.projectionEpoch !== status.epoch) window.location.reload();
              else this.projectionEpoch = status.epoch;
            }).catch(error => console.warn('Cannot check Hosanna adapter epoch', error));
        }
      });
      provider.on('sync', s => console.log(`Workspace ${wsId} sync status:`, s ? 'synced' : 'syncing'));

      doc.on('update', (upd, origin) => console.log(`Workspace ${wsId} update received:`, {
        updateLength: upd.length, origin: origin || 'unknown', hasOrigin: !!origin
      }));

      doc.on('subdocs', ({added, removed, loaded}) => console.log(`Workspace ${wsId} subdocs changed:`, {
        added: Array.from(added).length,
        removed: Array.from(removed).length,
        loaded: Array.from(loaded).length
      }));

      // If no file path, return just the doc and provider
      if (!filePath) {
        return { doc, provider };
      }

      // With file path, set up and return text and metadata too
      const contentPath = `content:${filePath}`;
      const metadataPath = `metadata:${filePath}`;
      const text = doc.getText(contentPath);
      const metadata = doc.getMap(metadataPath);
      if (!isWorkspaceReadOnly(wsId)) this._addFileToList(doc, filePath, wsId);

      return { doc, text, metadata, provider, filePath, contentPath, metadataPath };
    } catch (error) {
      console.error('Error connecting to Y.js document:', error);
      throw error;
    }
  }

  _addFileToList(doc, filePath, wsId) {
    assertWorkspaceWritable(wsId);
    const fileMap = doc.getMap('files');
    if (!fileMap.has(filePath)) fileMap.set(filePath, true);
  }

  _storeObserver(key, observer, type = 'observers') {
    const map = this[type];
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(observer);
  }

  subscribeToTextChanges(wsId, filePath, callback) {
    if (typeof filePath === 'function') {
      callback = filePath;
      throw new Error('File path is required');
    }
    if (!wsId || !callback || typeof callback !== 'function')
      throw new Error('Workspace ID and callback function are required');

    if (process.env.REACT_APP_HOSANNA_DIRECT === '1') {
      const {doc} = this.connectToDocument(wsId);
      let previous;
      const emit = () => {
        const content = doc.getText(`content:${filePath}`).toString();
        if (content === previous) return;
        previous = content; callback(content);
      };
      doc.on('update', emit); emit();
      return () => doc.off('update', emit);
    }
    try {
      const {doc, text, contentPath} = this.connectToDocument(wsId, filePath);
      const observer = () => callback(text.toString());
      text.observe(observer);

      const observerKey = `${wsId}:${contentPath}`;
      this._storeObserver(observerKey, observer);

      return () => {
        text.unobserve(observer);
        const observers = this.observers.get(observerKey);
        if (observers) {
          observers.delete(observer);
          if (observers.size === 0) this.observers.delete(observerKey);
        }
      };
    } catch (error) {
      console.error('Error subscribing to document changes:', error);
      throw error;
    }
  }

  subscribeToMetadataChanges(wsId, filePath, callback) {
    if (!wsId || !callback || typeof callback !== 'function')
      throw new Error('Workspace ID and callback function are required');

    try {
      const {metadata} = this.connectToDocument(wsId, filePath);
      const observer = () => {
        const metaObj = {};
        metadata.forEach((v, k) => metaObj[k] = v);
        callback(metaObj);
      };

      metadata.observe(observer);
      const observerKey = `${wsId}:metadata:${filePath}`;
      this._storeObserver(observerKey, observer);
      observer();

      return () => {
        metadata.unobserve(observer);
        const observers = this.observers.get(observerKey);
        if (observers) {
          observers.delete(observer);
          if (observers.size === 0) this.observers.delete(observerKey);
        }
      };
    } catch (error) {
      console.error('Error subscribing to metadata changes:', error);
      throw error;
    }
  }

  subscribeToDocumentEvents(wsId, filePath, callback) {
    // Handle case where filePath is actually the callback (making filePath optional)
    if (typeof filePath === 'function') {
      callback = filePath;
      filePath = undefined;
    }

    if (!wsId || !callback || typeof callback !== 'function')
      throw new Error('Workspace ID and callback function are required');

    try {
      const {doc} = this.connectToDocument(wsId, filePath);
      const updateObs = (upd, origin) => callback({
        type: 'document-update', workspaceId: wsId,
        update: {length: upd.length}, origin: origin || 'unknown'
      });

      const subdocsObs = ({added, removed, loaded}) => callback({
        type: 'subdocs-change', workspaceId: wsId,
        added: Array.from(added), removed: Array.from(removed), loaded: Array.from(loaded)
      });

      doc.on('update', updateObs);
      doc.on('subdocs', subdocsObs);

      if (!this.documentObservers.has(wsId)) this.documentObservers.set(wsId, new Map());
      const observers = this.documentObservers.get(wsId);
      const subscription = Symbol('document-events');
      observers.set(subscription, {update: updateObs, subdocs: subdocsObs});

      return () => {
        if (this.documentObservers.has(wsId)) {
          const obs = this.documentObservers.get(wsId);
          if (obs.has(subscription)) {
            doc.off('update', updateObs);
            doc.off('subdocs', subdocsObs);
            obs.delete(subscription);
          }
          if (obs.size === 0) this.documentObservers.delete(wsId);
        }
      };
    } catch (error) {
      console.error('Error subscribing to document events:', error);
      throw error;
    }
  }

  subscribeToAwarenessEvents(wsId, filePath, callback) {
    // Handle case where filePath is actually the callback (making filePath optional)
    if (typeof filePath === 'function') {
      callback = filePath;
      filePath = undefined;
    }

    if (!wsId || !callback || typeof callback !== 'function')
      throw new Error('Workspace ID and callback function are required');

    try {
      const {provider} = this.connectToDocument(wsId, filePath);
      if (!provider.awareness) throw new Error('Awareness is not available for this provider');

      const awarenessObs = ({added, updated, removed}) => {
        const states = provider.awareness.getStates(), users = [];
        states.forEach((state, clientId) => {
          if (state.user) users.push({clientId, ...state.user});
        });
        callback({type: 'awareness-change', workspaceId: wsId, users, changes: {added, updated, removed}});
      };

      provider.awareness.on('change', awarenessObs);
      this._storeObserver(wsId, awarenessObs, 'awarenessObservers');

      return () => {
        if (this.awarenessObservers.has(wsId)) {
          const observers = this.awarenessObservers.get(wsId);
          if (observers.has(awarenessObs)) {
            provider.awareness.off('change', awarenessObs);
            observers.delete(awarenessObs);
            if (observers.size === 0) this.awarenessObservers.delete(wsId);
          }
        }
      };
    } catch (error) {
      console.error('Error subscribing to awareness events:', error);
      throw error;
    }
  }

  subscribeToFileList(wsId, callback) {
    const {doc} = this.connectToDocument(wsId);
    const fileMap = doc.getMap('files');
    const observer = () => callback(Array.from(fileMap.keys()));
    fileMap.observe(observer);
    callback(Array.from(fileMap.keys()));
    return () => fileMap.unobserve(observer);
  }

  subscribeToPresentationState(wsId, callback) {
    if (!wsId || !callback || typeof callback !== 'function') {
      throw new Error('Workspace ID and callback function are required');
    }

    const {doc} = this.connectToDocument(wsId);
    const stateMap = doc.getMap('presentationState');

    const emitState = () => {
      const rawState = stateMap.get('current');
      const clonedState = rawState ? JSON.parse(JSON.stringify(rawState)) : null;
      callback(clonedState);
    };

    const observer = () => emitState();
    stateMap.observe(observer);
    emitState();

    return () => {
      stateMap.unobserve(observer);
    };
  }

  subscribeToGroupSelection(wsId, callback) {
    if (!wsId || typeof callback !== 'function') throw new Error('Workspace ID and callback function are required');
    const {doc} = this.connectToDocument(wsId);
    const selections = doc.getMap('fileBrowserSelections');
    let lastSelection;
    const emit = () => {
      const raw = selections.get('latestSelection');
      const serialized = JSON.stringify(raw || null);
      if (serialized === lastSelection) return;
      lastSelection = serialized;
      callback(JSON.parse(serialized));
    };
    selections.observe(emit);
    emit();
    return () => selections.unobserve(emit);
  }

  updatePresentationState(wsId, state) {
    if (!wsId) throw new Error('Workspace ID is required');
    const {doc} = this.connectToDocument(wsId);
    const stateMap = doc.getMap('presentationState');

    doc.transact(() => {
      stateMap.set('current', state || null);
    });
  }

  clearPresentationState(wsId) {
    if (!wsId) throw new Error('Workspace ID is required');
    const {doc} = this.connectToDocument(wsId);
    const stateMap = doc.getMap('presentationState');

    doc.transact(() => {
      stateMap.delete('current');
    });
  }

  updateDocument(wsId, content, filePath) {
    assertWorkspaceWritable(wsId);
    if (!wsId) throw new Error('Workspace ID is required');
    if (!filePath) throw new Error('File path is required');
    try {
      if (process.env.REACT_APP_HOSANNA_DIRECT === '1') {
        this.connectToDocument(wsId); this.hosannaWorkspaces.get(wsId).ensureFile(filePath);
      }
      //console.log('Updating document 1:', {wsId, filePath, content});
      const {doc, text} = this.connectToDocument(wsId, filePath);
      this._addFileToList(doc, filePath, wsId)
      if (text.toString() === content) return;
      //console.log('Updating document 2:', {wsId, filePath, content});
      doc.transact(() => {
        const curr = text.toString();
        const diffs = diffChars(curr, content);
        let pos = 0;
        diffs.forEach(part => {
          if (part.added) {
            text.insert(pos, part.value);
            pos += part.value.length;
          } else if (part.removed) {
            text.delete(pos, part.value.length);
          } else {
            pos += part.value.length;
          }
        });
      });
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  updateMetadata(wsId, metaUpdate, filePath) {
    assertWorkspaceWritable(wsId);
    if (!wsId) throw new Error('Workspace ID is required');
    if (!filePath) throw new Error('File path is required');
    try {
      const {doc, metadata, metadataPath} = this.connectToDocument(wsId, filePath);
      if (!metadata) throw new Error('Metadata map not found');
      // list current metadata
      const currentMetadata = {};
      metadata.forEach((v, k) => currentMetadata[k] = v);
      console.log('Current metadata:', currentMetadata, metadataPath);

      doc.transact(() => {
        for (const [k, v] of Object.entries(metaUpdate)) metadata.set(k, v);
      });
    } catch (error) {
      console.error('Error updating metadata:', error);
      throw error;
    }
  }

  getDocumentContent(wsId, filePath) {
    if (!wsId) throw new Error('Workspace ID is required');
    if (!filePath) throw new Error('File path is required');
    try {
      return this.connectToDocument(wsId, filePath).text.toString();
    } catch (error) {
      console.error('Error getting document content:', error);
      throw error;
    }
  }

  getDocumentMetadata(wsId, filePath) {
    if (!wsId) throw new Error('Workspace ID is required');
    if (!filePath) throw new Error('File path is required');
    try {
      const {doc} = this.connectToDocument(wsId, filePath);
      const metadataMap = doc.getMap(`metadata:${filePath}`);
      const metadata = {};
      metadataMap.forEach((value, key) => {
        metadata[key] = value;
      });
      return metadata;
    } catch (error) {
      console.error('Error getting document metadata:', error);
      throw error;
    }
  }

  getAwarenessInfo(wsId) {
    if (!wsId || !this.providers.has(wsId)) return [];
    try {
      const provider = this.providers.get(wsId);
      if (!provider.awareness) return [];

      const states = provider.awareness.getStates(), users = [];
      const ydoc = this.docs.get(wsId);

      states.forEach((state, clientId) => { // clientId here is the Yjs internal client ID
        if (process.env.REACT_APP_HOSANNA_DIRECT === '1') {
          if (state.user?.name) users.push({clientId, ...state.user, id: state.user.id || String(clientId), ipAddress: null});
          return;
        }
        // Ensure user object and the user.id (deviceGuid) are present in the state
        if (state.user && state.user.id) { 
          let ipAddress = null;
          const deviceGuid = state.user.id; // Get the deviceGuid from the user state

          try {
            // Construct the lookup key using the deviceGuid, matching the server-side key
            const clientInfoKey = `clientInfo-${deviceGuid}`; 
            const clientInfo = ydoc.getMap(clientInfoKey);
            
            // Check if the map exists and has the 'remoteIp' key
            if (clientInfo && clientInfo.has('remoteIp')) {
              ipAddress = clientInfo.get('remoteIp');
            }
          } catch (err) {
            // Log errors during lookup, referencing the deviceGuid for clarity
            console.error(`Error getting client info for deviceGuid ${deviceGuid} (Yjs clientId ${clientId}):`, err);
          }

          users.push({
            clientId, // Keep the internal Yjs clientId if needed
            ...state.user, // Spread user info (name, id which is deviceGuid)
            ipAddress // Add the retrieved IP address (will be null if not found)
          });
        } 
      });
      return users;
    } catch (error) {
      console.error('Error getting awareness info:', error);
      return [];
    }
  }

  getUserSettings(wsId, deviceGuid = null) {
    if (!wsId) throw new Error('Workspace ID is required');
    
    try {
      const { doc } = this.connectToDocument(wsId);
      const userSettingsMap = doc.getMap('userSettings');
      const currentDeviceGuid = deviceGuid || getDeviceInfo().guid;
      
      // If this device doesn't have settings yet, return empty object
      if (!userSettingsMap.has(currentDeviceGuid)) {
        return {};
      }
      
      // Get the device's settings map
      const deviceSettings = userSettingsMap.get(currentDeviceGuid);
      
      // Convert to plain object
      const settingsObj = {};
      if (typeof deviceSettings === 'object' && deviceSettings !== null) {
        Object.entries(deviceSettings).forEach(([key, value]) => {
          settingsObj[key] = value;
        });
      }
      
      return settingsObj;
    } catch (error) {
      console.error('Error getting user settings:', error);
      return {};
    }
  }

  updateUserSettings(wsId, settings) {
    if (!wsId) throw new Error('Workspace ID is required');
    if (!settings || typeof settings !== 'object') throw new Error('Settings must be an object');
    
    try {
      const { doc } = this.connectToDocument(wsId);
      const userSettingsMap = doc.getMap('userSettings');
      const deviceGuid = getDeviceInfo().guid;
      
      doc.transact(() => {
        // Get existing settings for this device or create new object
        let deviceSettings = userSettingsMap.get(deviceGuid) || {};
        
        // Update with new settings
        deviceSettings = { ...deviceSettings, ...settings };
        
        // Store back in the map
        userSettingsMap.set(deviceGuid, deviceSettings);
      });
      
      return true;
    } catch (error) {
      console.error('Error updating user settings:', error);
      return false;
    }
  }

  // Add a method to get all devices' settings
  getAllUserSettings(wsId) {
    if (!wsId) throw new Error('Workspace ID is required');
    
    try {
      const { doc } = this.connectToDocument(wsId);
      const userSettingsMap = doc.getMap('userSettings');
      
      // Convert YJS Map to a nested object by device ID
      const allSettings = {};
      userSettingsMap.forEach((settings, deviceId) => {
        allSettings[deviceId] = settings;
      });
      
      return allSettings;
    } catch (error) {
      console.error('Error getting all user settings:', error);
      return {};
    }
  }

  disconnectFromDocument(wsId) {
    if (!wsId) return;
    try {
      if (this.providers.has(wsId)) {
        const provider = this.providers.get(wsId);
        if (this.awarenessObservers.has(wsId) && provider.awareness) {
          this.awarenessObservers.get(wsId).forEach(o => provider.awareness.off('change', o));
          this.awarenessObservers.delete(wsId);
        }
        if (process.env.REACT_APP_HOSANNA_DIRECT === '1' && provider.destroy) provider.destroy();
        else provider.disconnect();
        this.providers.delete(wsId);
      }

      if (this.documentObservers.has(wsId) && this.docs.has(wsId)) {
        const doc = this.docs.get(wsId);
        this.documentObservers.get(wsId).forEach(({update, subdocs}) => {
          doc.off('update', update);
          doc.off('subdocs', subdocs);
        });
        this.documentObservers.delete(wsId);
      }

      for (const k of this.observers.keys()) {
        if (k.startsWith(`${wsId}:`)) this.observers.delete(k);
      }

      if (this.docs.has(wsId)) {
        this.docs.get(wsId).destroy();
        this.docs.delete(wsId);
        this.hosannaWorkspaces?.delete(wsId);
      }
    } catch (error) {
      console.error('Error disconnecting from document:', error);
    }
  }

  destroy() {
    try {
      [...this.providers.keys()].forEach(wsId => this.disconnectFromDocument(wsId));
      this.docs.clear();
      this.providers.clear();
      this.observers.clear();
      this.documentObservers.clear();
      this.awarenessObservers.clear();
    } catch (error) {
      console.error('Error destroying YjsDocumentService:', error);
    }
  }

  deleteFileFromList(wsId, filePath) {
    assertWorkspaceWritable(wsId);
    if (!wsId) throw new Error('Workspace ID is required');
    if (!filePath) throw new Error('File path is required');

    if (process.env.REACT_APP_HOSANNA_DIRECT === '1') {
      this.connectToDocument(wsId); this.hosannaWorkspaces.get(wsId).deletePath(filePath); return;
    }
    const {doc} = this.connectToDocument(wsId, filePath);

    const fileMap = doc.getMap('files');
    if (fileMap.has(filePath)) fileMap.delete(filePath);

    try {
      const contentPath = `content:${filePath}`;
      doc.getText(contentPath).delete(0, doc.getText(contentPath).length);
    } catch (e) {
      console.error(`Error deleting text content for ${filePath}:`, e);
    }

    try {
      const metadataPath = `metadata:${filePath}`;
      const m = doc.getMap(metadataPath);
      m.forEach((_, k) => m.delete(k));
    } catch (e) {
      console.error(`Error deleting metadata for ${filePath}:`, e);
    }
  }

  renameFile(wsId, oldPath, newPath) {
    this.connectToDocument(wsId);
    return this.hosannaWorkspaces.get(wsId).renamePath(oldPath, newPath);
  }

  getFileList(workspaceId) {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    try {
      const {doc} = this.connectToDocument(workspaceId);
      const fileMap = doc.getMap('files');
      return Array.from(fileMap.keys());
    } catch (error) {
      console.error('Error getting file list:', error);
      throw error;
    }
  }


}

const yjsDocumentService = new YjsDocumentService();
export default yjsDocumentService;
